
import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  DataTable,
  Text,
  Spinner,
  BlockStack,
  InlineStack,
  Banner,
  Tooltip,
  Icon,
  Tooltip as ToolTip,
} from '@shopify/polaris';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { InfoIcon, ArrowUpIcon, ArrowDownIcon } from '@shopify/polaris-icons';
import axios from 'axios';
import { format, parseISO, differenceInDays, subDays } from 'date-fns';


function AllPostTable({ shopOrigin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();


  const dataType = searchParams.get('type');
  const since = searchParams.get('since');
  const until = searchParams.get('until');
  const articleId = searchParams.get('articleId');
  const category = searchParams.get('category');


  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [banner, setBanner] = useState({ show: false, message: '', status: 'success' });


  useEffect(() => {
    if (!shopOrigin || !dataType || !since || !until) {
      setBanner({ show: true, message: 'Missing required parameters', status: 'critical' });
      setLoading(false);
      return;
    }
    fetchData();
  }, [shopOrigin, dataType, since, until, articleId, category]);


  const getTrend = (current, previous) => {
    if (previous === null || previous === undefined) return 0;
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };


  // ✅ Trend renderer using Text with tone (success, critical, subdued)
  const renderTrend = (trend) => {
    if (trend === null || trend === undefined) {
      return <Text as="span" tone="subdued">-</Text>;
    }
    if (trend === 0) {
      return <Text as="span" tone="subdued">0%</Text>;
    }


    const isPositive = trend > 0;
    const tone = isPositive ? 'success' : 'critical';
    const icon = isPositive ? ArrowUpIcon : ArrowDownIcon;


    return (
      <InlineStack gap="100" blockAlign="end">
        <Icon source={icon} tone={tone} />
        <Text as="span" variant="bodySm" tone={tone} fontWeight="medium">
          {`${Math.abs(trend)}%`}
        </Text>
      </InlineStack>
    );
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      const sinceDate = parseISO(since);
      const untilDate = parseISO(until);
      const duration = differenceInDays(untilDate, sinceDate);
      const previousUntil = subDays(sinceDate, 1);
      const previousSince = subDays(previousUntil, duration);


      const prevSinceStr = format(previousSince, 'yyyy-MM-dd');
      const prevUntilStr = format(previousUntil, 'yyyy-MM-dd');


      let rows = [];


      if (dataType === 'sessions') {
        // =========================
        // Sessions Data
        // =========================
        const currentParams = new URLSearchParams({
          shop: shopOrigin,
          since,
          until,
          limit: 'All',
        });
        const prevParams = new URLSearchParams({
          shop: shopOrigin,
          since: prevSinceStr,
          until: prevUntilStr,
          limit: 'All',
        });
        if (articleId) {
          currentParams.append('articleid', articleId);
          prevParams.append('articleid', articleId);
        }
        if (category) {
          currentParams.append('category', category);
          prevParams.append('category', category);
        }


        const [postsResponse, prevPostsResponse] = await Promise.all([
          axios.get(`/analytics/top-posts/?${currentParams.toString()}`),
          axios.get(`/analytics/top-posts/?${prevParams.toString()}`),
        ]);


        const prevViewsMap = new Map(prevPostsResponse.data.map((item) => [item.article_id, item.views || 0]));
        const combinedData = postsResponse.data.map((post) => ({
          ...post,
          viewsTrend: post.trend ?? 0, // ✅ Use backend-calculated trend
        }));



        combinedData.sort((a, b) => (b.views || 0) - (a.views || 0));


        rows = combinedData.map((post) => {
          const dashboardFilters = location.state?.dashboardFilters;
          const queryParams = new URLSearchParams({
            shop: shopOrigin || '',
            since: since,
            until: until
          });

          if (dashboardFilters?.category) {
            queryParams.append('category', dashboardFilters.category);
          }

          const titleLink = (
            <Link
              to={`/article/${post.article_id}?${queryParams.toString()}`}
              style={{ color: '#005BD3', textDecoration: 'none' }}
              state={{
                articleTitle: post.title || 'Untitled',
                publishedDate: post.published_at,
                authorName: post.author,
                categoryName: post.category,
                articleId: post.article_id,
              }}
            >
              {post.title || 'Untitled'}
            </Link>
          );
          const viewsCell = (
            <InlineStack gap="150" blockAlign="center" align="end">
              <ToolTip content="Total number of views for this article">
                <Text as="span">{post.views?.toString() || '0'}</Text>
              </ToolTip>
            </InlineStack>
          );
          const trendCell = renderTrend(post.viewsTrend);
          return [titleLink, viewsCell, trendCell];
        });
      } else if (dataType === 'engagement') {
        // =========================
        // Engagement Data
        // =========================
        const currentParams = new URLSearchParams({
          shop: shopOrigin,
          since,
          until,
          limit: 'All',
        });
        const prevParams = new URLSearchParams({
          shop: shopOrigin,
          since: prevSinceStr,
          until: prevUntilStr,
          limit: 'All',
        });
        if (articleId) {
          currentParams.append('articleid', articleId);
          prevParams.append('articleid', articleId);
        }
        if (category) {
          currentParams.append('category', category);
          prevParams.append('category', category);
        }


        const [engagementResponse, prevEngagementResponse] = await Promise.all([
          axios.get(`/analytics/engagement/?${currentParams.toString()}`),
          axios.get(`/analytics/engagement/?${prevParams.toString()}`),
        ]);


        const engagementMap = new Map(
          engagementResponse.data.per_article.map((item) => [
            item.article_id,
            item.engagement_percentage ?? 0,
          ])
        );


        const prevEngagementMap = new Map(
          prevEngagementResponse.data.per_article.map((item) => [
            item.article_id,
            item.engagement_percentage ?? 0,
          ])
        );


        const combinedData = engagementResponse.data.per_article.map((post) => {
          const engagement = engagementMap.get(post.article_id) ?? 0;
          const prevEngagement = prevEngagementMap.get(post.article_id) ?? 0;
          return {
            ...post,
            engagement,
            engagementTrend: getTrend(engagement, prevEngagement),
          };
        });


        combinedData.sort((a, b) => (b.engagement || 0) - (a.engagement || 0));


        rows = combinedData.map((post) => {
          const dashboardFilters = location.state?.dashboardFilters;
          const queryParams = new URLSearchParams({
            shop: shopOrigin || '',
            since: since,
            until: until
          });

          if (dashboardFilters?.category) {
            queryParams.append('category', dashboardFilters.category);
          }

          const titleLink = (
            <Link
              to={`/article/${post.article_id}?${queryParams.toString()}`}
              style={{ color: '#005BD3', textDecoration: 'none' }}
              state={{
                articleTitle: post.title || 'Untitled',
                publishedDate: post.published_at,
                authorName: post.author,
                categoryName: post.category,
                articleId: post.article_id,
              }}
            >
              {post.title || 'Untitled'}
            </Link>
          );
          const engagementCell = <Text as="span">{post.engagement?.toFixed(1)}%</Text>;
          const trendCell = renderTrend(post.engagementTrend);
          return [titleLink, engagementCell, trendCell];
        });
      } else if (dataType === 'products') {
        // =========================
        // Clicked Products Data
        // =========================
        const currentParams = new URLSearchParams({
          shop: shopOrigin,
          since,
          until,
          limit: 'All',
        });

        if (articleId) {
          currentParams.append('articleid', articleId);
        }
        if (category) {
          currentParams.append('category', category);
        }

        const response = await axios.get(`/analytics/top-clicked-products/?${currentParams.toString()}`);

        const combinedData = response.data.map(product => ({
          ...product
        }));

        combinedData.sort((a, b) => (b.clicks || 0) - (a.clicks || 0));

        rows = combinedData.map((product) => {
          const titleCell = <Text as="span">{product.title || 'Untitled Product'}</Text>;
          const clicksCell = (
            <InlineStack gap="150" blockAlign="center" align="end">
              <ToolTip content="Total number of clicks for this product">
                <Text as="span">{product.clicks?.toString() || '0'}</Text>
              </ToolTip>
            </InlineStack>
          );
          const trendCell = renderTrend(product.trend);
          return [titleCell, clicksCell, trendCell];
        });
      } else {
        throw new Error('Invalid data type');
      }


      setData(rows);
    } catch (error) {
      console.error('Error fetching data:', error);
      setBanner({ show: true, message: `Failed to load ${dataType} data`, status: 'critical' });
      setData([]);
    } finally {
      setLoading(false);
    }
  };


  const createBoldHeading = (text) => <span style={{ fontWeight: 600, fontSize: '13px' }}>{text}</span>;
  const createHeaderWithTooltip = (label, tooltipContent) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
      <span style={{ fontWeight: 600, fontSize: '13px' }}>{label}</span>
      <Tooltip content={tooltipContent} dismissOnMouseOut>
        <Icon source={InfoIcon} tone="base" />
      </Tooltip>
    </div>
  );


  const getTitle = () => {
    switch (dataType) {
      case 'sessions': return 'All Blog Posts by Sessions';
      case 'engagement': return 'All Blog Posts by Engagement';
      case 'products': return 'All Clicked Products';
      default: return 'All Posts';
    }
  };


  const getHeadings = () => {
    switch (dataType) {
      case 'sessions':
        return [
          createBoldHeading('ARTICLE'),
          createHeaderWithTooltip('SESSIONS', 'Total number of views for this article'),
          createHeaderWithTooltip('TREND', 'Change in views vs. previous period'),
        ];
      case 'engagement':
        return [
          createBoldHeading('ARTICLE'),
          createHeaderWithTooltip('ENGAGEMENT', 'Overall engagement score combining time spent & scroll rate'),
          createHeaderWithTooltip('TREND', 'Change in engagement vs. previous period'),
        ];
      case 'products':
        return [
          createBoldHeading('PRODUCT'),
          createHeaderWithTooltip('CLICKS', 'Total number of clicks for this product'),
          createHeaderWithTooltip('TREND', 'Change in clicks vs. previous period'),
        ];
      default:
        return [];
    }
  };


  const getColumnContentTypes = () => {
    switch (dataType) {
      case 'sessions': return ['text', 'numeric', 'text'];
      case 'engagement': return ['text', 'numeric', 'text'];
      case 'products': return ['text', 'numeric', 'text'];
      default: return ['text', 'text'];
    }
  };


  return (
    <Page
      title={getTitle()}
      backAction={{
        content: 'Dashboard',
        onAction: () => navigate(`/dashboard?shop=${encodeURIComponent(shopOrigin)}`),
      }}
    >
      {banner.show && (
        <Banner status={banner.status} onDismiss={() => setBanner({ ...banner, show: false })}>
          {banner.message}
        </Banner>
      )}
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">{getTitle()}</Text>
            <Text as="p" variant="bodySm" tone="subdued">Total: {data.length} items</Text>
          </InlineStack>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Spinner size="large" />
            </div>
          ) : data.length > 0 ? (
            <DataTable
              columnContentTypes={getColumnContentTypes()}
              headings={getHeadings()}
              rows={data}
              hoverable
            />
          ) : (
            <Text as="p" tone="subdued" alignment="center">No data available for the selected period.</Text>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}


export default AllPostTable;
